import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogOverlay,
  AlertDialogPortal,
} from '@/components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';

interface FormulaValidationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedFormulaName: string;
  fallbackFormulaName: string;
  onConfirm: () => void;
}

/**
 * Modal that displays when a user selects a BMR formula that is not 
 * recommended/viable for the client's profile (age, gender, country).
 */
const FormulaValidationModal: React.FC<FormulaValidationModalProps> = ({
  open,
  onOpenChange,
  selectedFormulaName,
  fallbackFormulaName,
  onConfirm,
}) => {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogPortal>
        <AlertDialogOverlay className="z-[1600]" />
        <AlertDialogContent className="max-w-md z-[1601]">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <AlertDialogTitle className="text-lg font-semibold">
                BMR Formula Not Validated
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-sm text-muted-foreground">
              <div className="space-y-3">
                <p>
                  Not validated for children. Use{' '}
                  <span className="font-medium text-foreground">{fallbackFormulaName}</span>.
                </p>
                <p className="text-xs text-muted-foreground/80">
                  Selected formula: <span className="font-medium">{selectedFormulaName}</span>
                </p>
                <p className="text-xs text-muted-foreground/80 pt-2 border-t border-border">
                  The formula has been switched to the age-appropriate option.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={handleConfirm}
              className="bg-primary hover:bg-primary/90"
            >
              Got it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogPortal>
    </AlertDialog>
  );
};

export default FormulaValidationModal;
